/**
 * 網站雛型管理（porsche 2026-08-13 · 目錄上傳 pivot）。
 * 選整個目錄 → 逐檔續傳（每檔小·繞開單一大 zip 的 nginx/RAM/逾時/連線池限制）→ 另開新頁檢視。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

import FunctionPageHeader from '../../../components/FunctionPageHeader';
import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';
import {
  listPrototypes, createPrototype, uploadDirectory, updatePrototype, deletePrototype,
  previewPublicUrl, WebsitePrototype,
} from './services/eeWebsitePrototypeService';
import { formatLocal } from '../../../utils/dateHelper';

const FUNC = 'ee_website_prototype';

const EeWebsitePrototypePage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showError, showApiError } = useMessage();
  const wp = (k: string, fb: string) => t(`websitePrototype.${k}`, fb);

  const canRead = hasPermission(FUNC, 'read');
  const canCreate = hasPermission(FUNC, 'create');
  const canUpdate = hasPermission(FUNC, 'update');
  const canDelete = hasPermission(FUNC, 'delete');

  const [rows, setRows] = useState<WebsitePrototype[]>([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState<{ mode: 'create' | 'edit'; row?: WebsitePrototype } | null>(null);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const dirInputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await listPrototypes()); }
    catch (err) { showApiError(err); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (!permLoading && canRead) load(); }, [permLoading, canRead, load]);

  const openCreate = () => { setName(''); setDesc(''); setFiles([]); setProgress(null); setDlg({ mode: 'create' }); };
  const openEdit = (row: WebsitePrototype) => {
    setName(row.name); setDesc(row.description || ''); setFiles([]); setProgress(null); setDlg({ mode: 'edit', row });
  };

  const totalMB = files.reduce((s, f) => s + f.size, 0) / 1024 / 1024;

  const runUpload = async (id: number) => {
    setProgress({ done: 0, total: files.length });
    const { failed } = await uploadDirectory(id, files, (done, total) => setProgress({ done, total }));
    if (failed.length) showError('ERR410007', { count: String(failed.length) });
  };

  const submit = async () => {
    if (!dlg) return;
    if (dlg.mode === 'create' && files.length === 0) return;
    setSaving(true);
    try {
      if (dlg.mode === 'create') {
        const created = await createPrototype(name, desc);
        await runUpload(created.id);
        showSuccess('SYS410001', { name });
      } else {
        // 編輯：更新 metadata；若有選新目錄 → replace 清空後重傳
        await updatePrototype(dlg.row!.id, name, desc, files.length > 0);
        if (files.length > 0) await runUpload(dlg.row!.id);
        showSuccess('SYS410002', { name });
      }
      setDlg(null);
      await load();
    } catch (err) { showApiError(err); }
    finally { setSaving(false); setProgress(null); }
  };

  const doDelete = async (row: WebsitePrototype) => {
    if (!window.confirm(wp('confirmDelete', '確定刪除此雛型？'))) return;
    try {
      await deletePrototype(row.id);
      showSuccess('SYS410003', { name: row.name });
      await load();
    } catch (err) { showApiError(err); }
  };

  if (permLoading) return null;
  if (!canRead) {
    return (
      <Box>
        <FunctionPageHeader funcCode={FUNC} />
        <Alert severity="warning" sx={{ m: 3 }}>{t('common.noPermission', '您沒有權限存取此功能')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <FunctionPageHeader funcCode={FUNC} />
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">{wp('pageTitle', '網站雛型管理')}</Typography>
          <Button variant="contained" disabled={!canCreate} onClick={openCreate}>
            {wp('upload', '上傳雛型')}
          </Button>
        </Stack>

        <Paper variant="outlined">
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{wp('name', '名稱')}</TableCell>
                  <TableCell>{wp('description', '說明')}</TableCell>
                  <TableCell align="center">{wp('version', '版本')}</TableCell>
                  <TableCell>{wp('uploadedAt', '上傳時間')}</TableCell>
                  <TableCell align="right">{t('common.actions', '操作')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    {t('common.noData', '無資料')}
                  </TableCell></TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.description || '—'}</TableCell>
                    <TableCell align="center">v{r.version}</TableCell>
                    <TableCell>{formatLocal(r.updated_at, '—')}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" title={wp('newTab', '檢視（另開新頁）')}
                        onClick={() => window.open(previewPublicUrl(r), '_blank')}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" disabled={!canUpdate} title={wp('edit', '修改')}
                        onClick={() => openEdit(r)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" disabled={!canDelete} title={wp('delete', '刪除')}
                        onClick={() => doDelete(r)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>

      <Dialog open={!!dlg} onClose={() => !saving && setDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{dlg?.mode === 'edit' ? wp('edit', '修改') : wp('upload', '上傳雛型')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={wp('name', '名稱')} value={name} onChange={(e) => setName(e.target.value)}
              required fullWidth size="small" />
            <TextField label={wp('description', '說明')} value={desc} onChange={(e) => setDesc(e.target.value)}
              fullWidth size="small" multiline minRows={2} />
            <Button variant="outlined" component="label" disabled={saving}>
              {files.length > 0
                ? `${wp('selectedDir', '已選目錄')}：${files.length} ${wp('filesUnit', '個檔案')}（${totalMB.toFixed(1)}MB）`
                : wp('pickDir', '選擇網站目錄')}
              <input
                type="file" hidden multiple
                ref={(node) => {
                  dirInputRef.current = node;
                  if (node) { node.setAttribute('webkitdirectory', ''); node.setAttribute('directory', ''); }
                }}
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
            </Button>
            <Typography variant="caption" color="text.secondary">
              {dlg?.mode === 'edit'
                ? wp('replaceDirHint', '不選目錄＝只改名稱/說明；選新目錄＝清空重傳整個網站')
                : wp('uploadDirHint', '選擇整個網站資料夾（逐檔上傳·可斷點續傳·單檔 ≤50MB）')}
            </Typography>
            {progress && (
              <Box>
                <LinearProgress variant="determinate"
                  value={progress.total ? (progress.done / progress.total) * 100 : 0} />
                <Typography variant="caption" color="text.secondary">
                  {wp('uploading', '上傳中')} {progress.done} / {progress.total}
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(null)} disabled={saving}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" onClick={submit}
            disabled={saving || !name || (dlg?.mode === 'create' && files.length === 0)}>
            {saving
              ? <><CircularProgress size={16} sx={{ mr: 1 }} />{t('common.loading', '載入中...')}</>
              : t('common.confirm', '確認')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EeWebsitePrototypePage;
